import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { ShortVideo } from '@/components/ShortVideo';
import {
  excelCourse, excelModules, excelClasses, upcomingClasses, classPath, moduleTotal, moduleRange,
  moduleCount, moduleLabel, moduleAnchor, lengthLabel, levelLabel, SITE, type ExcelLevel, type ExcelModule,
} from '@/content/excelCourse';

const levelTone: Record<ExcelLevel, string> = {
  basics: 'bg-econ-50 text-econ-600 border-econ-100',
  intermediate: 'bg-acct-50 text-acct-600 border-acct-100',
  advanced: 'bg-biz-50 text-biz-600 border-biz-100',
  pro: 'bg-accent-50 text-accent-700 border-accent-200/70',
};

export function LevelPill({ level }: { level: ExcelLevel }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${levelTone[level]}`}>
      {levelLabel[level]}
    </span>
  );
}

/** "Excel 2019 or newer" / "Microsoft 365" for an upcoming class. */
export const needsLabel = (v: string) => (v === '365' ? 'Needs Microsoft 365' : `Needs Excel ${v} or newer`);

const HOW_ITS_MADE = [
  {
    title: 'Real Excel, every time',
    body: 'Every cell, formula and result on screen is what Microsoft Excel actually displayed. Each class is replayed in real Excel before it is made.',
  },
  {
    title: 'An AI moment, checked',
    body: 'Each class has a prompt that works in ChatGPT, Gemini, Copilot or Claude. The AI’s real answer is pasted in and checked in Excel. When the AI got it wrong, the class shows that, so you learn to test AI in Excel.',
  },
  {
    title: 'Windows and Mac',
    body: 'Shortcuts are shown for both. Feature and shortcut facts come from Microsoft’s own support pages, linked on every class page.',
  },
  {
    title: 'One topic, 1–2.5 minutes',
    body: 'Each class teaches one thing, as a vertical video made for your phone. Watch it on the bus, then try it in Excel.',
  },
];

/** "Modules 1–6 are complete and module 7 (Dates & times) is under way." */
function progressLine(): string {
  const complete = excelModules.filter((m) => m.made >= moduleTotal(m)).length;
  const pm = excelModules.find((m) => m.made > 0 && m.made < moduleTotal(m));
  const parts: string[] = [];
  if (complete > 0) parts.push(complete === 1 ? 'Module 1 is complete' : `Modules 1–${complete} are complete`);
  if (pm) parts.push(`${moduleLabel(pm).toLowerCase()} (${pm.name}) is under way`);
  return parts.length ? `${parts.join(' and ')}.` : '';
}

export function ExcelCoursePage() {
  const [playing, setPlaying] = useState<number | null>(null);
  const pct = Math.round((excelCourse.made / excelCourse.planned) * 100);
  const url = `${SITE}/excel-course/`;
  const description = `A free Microsoft Excel course from basics to advanced to pro: ${excelCourse.planned} short video classes in ${moduleCount} modules, ${excelCourse.made} ready now. Every result is checked in real Excel, with an AI prompt in each class.`;

  return (
    <div>
      <SEO
        title="Free Excel Course — Excel Study, basics to pro in short classes | ACES"
        description={description}
        canonical={url}
        ogImage={`${SITE}${excelClasses[0]?.thumbnail ?? ''}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Course',
              name: `${excelCourse.name} — free Microsoft Excel course`,
              description,
              url,
              inLanguage: 'en',
              isAccessibleForFree: true,
              provider: { '@type': 'Organization', name: excelCourse.by, sameAs: 'https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw' },
            },
            {
              '@type': 'ItemList',
              name: `${excelCourse.name} classes`,
              itemListElement: excelClasses.map((c, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `${SITE}${classPath(c.class)}`,
                name: `Class ${c.class}: ${c.topic}`,
              })),
            },
          ],
        }}
      />

      {/* Hero */}
      <section className="aces-hero">
        <div className="max-w-5xl mx-auto px-4 pt-12 sm:pt-16 pb-10">
          <nav className="text-xs text-ink-500 mb-6">
            <Link to="/" className="hover:text-accent-600">Home</Link>
            <span className="text-ink-300 mx-1.5">·</span>
            <span className="text-ink-600">Excel course</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-600 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            Free course · {excelCourse.name}
          </span>
          <h1 className="font-serif text-[2.3rem] sm:text-5xl lg:text-[3.3rem] text-ink-900 tracking-tight leading-[1.06] max-w-3xl text-balance">
            Learn Microsoft Excel, <em className="italic text-accent-600">one short class</em> at a time
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl leading-relaxed">
            {excelCourse.planned} short classes in {moduleCount} modules, from your first formula
            to pro-level tools. One topic per class, 1–2.5 minutes each, made as vertical videos for your
            phone. By {excelCourse.by}.
          </p>

          {/* Progress */}
          <div className="mt-8 max-w-xl rounded-card border border-ink-200/80 bg-cream p-5 shadow-card">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-serif text-xl text-ink-900">
                <span className="tabular-nums">{excelCourse.made}</span> of <span className="tabular-nums">{excelCourse.planned}</span> classes ready
              </span>
              <span className="text-xs text-ink-500 tabular-nums">{pct}%</span>
            </div>
            <div
              className="mt-3 h-2.5 rounded-full bg-ink-100 overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={excelCourse.planned}
              aria-valuenow={excelCourse.made}
              aria-label="Classes ready"
            >
              <div className="h-full rounded-full bg-accent-500" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-3 text-sm text-ink-600">{progressLine()} More classes coming.</p>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            {excelClasses[0] && (
              <Link to={classPath(excelClasses[0].class)} className="aces-btn-primary">Start with Class 1 →</Link>
            )}
            <a
              href="#modules"
              className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-ink-700 border border-ink-200 bg-cream hover:border-ink-300 hover:bg-white transition-colors duration-150"
            >
              See all {moduleCount} modules
            </a>
          </div>
        </div>
      </section>

      {/* How every class is made */}
      <section className="bg-cream border-y border-ink-200">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="font-serif text-[1.7rem] text-ink-900 tracking-tight mb-6">How every class is made</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {HOW_ITS_MADE.map((f) => (
              <div key={f.title} className="rounded-card border border-ink-200/80 bg-paper p-5">
                <h3 className="font-serif text-lg text-ink-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-ink-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Module map */}
      <section id="modules" className="bg-ink-50 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="font-serif text-[1.7rem] text-ink-900 tracking-tight">The {moduleCount} modules</h2>
          <p className="text-sm text-ink-500 mt-1 mb-6">Basics → intermediate → advanced → pro. Tap a module to jump to its classes.</p>
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {excelModules.map((m) => (
              <li key={m.name}>
                <ModuleTile m={m} />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Classes, module by module */}
      {excelModules.map((m) => {
        const [a, b] = moduleRange(m);
        const classes = excelClasses.filter((c) => c.class >= a && c.class <= b);
        if (classes.length === 0) return null;
        return (
          <section key={m.name} id={moduleAnchor(m)} className="border-t border-ink-200 bg-paper scroll-mt-20">
            <div className="max-w-6xl mx-auto px-4 py-12">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-6">
                <div>
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-600 mb-1">
                    {moduleLabel(m)} · Classes {m.classes.replace('-', '–')}
                  </span>
                  <h2 className="font-serif text-2xl md:text-[1.8rem] text-ink-900 tracking-tight">{m.name}</h2>
                </div>
                <span className="text-sm text-ink-500 tabular-nums">{m.made} of {moduleTotal(m)} ready</span>
              </div>
              <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {classes.map((c) => (
                  <li
                    key={c.class}
                    className="flex sm:flex-col gap-4 sm:gap-0 rounded-card border border-ink-200/80 bg-cream p-3 sm:p-0 shadow-card overflow-hidden"
                  >
                    <div className="w-[40%] max-w-[170px] shrink-0 sm:w-full sm:max-w-none rounded-lg sm:rounded-none overflow-hidden">
                      <ShortVideo
                        videoId={c.video_id}
                        title={`Excel Class ${c.class}: ${c.topic}`}
                        poster={c.thumbnail}
                        playing={playing === c.class}
                        onPlay={() => setPlaying(c.class)}
                      />
                    </div>
                    <div className="min-w-0 flex flex-col sm:p-4">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-600">
                        Class {c.class} · {lengthLabel(c.length_seconds)}
                      </span>
                      <h3 className="font-serif text-[1.15rem] leading-snug text-ink-900 mt-1">
                        <Link to={classPath(c.class)} className="hover:text-accent-700">{c.topic}</Link>
                      </h3>
                      <div className="mt-2"><LevelPill level={c.level} /></div>
                      <p className="mt-2 text-sm text-ink-600 leading-snug">{c.description}</p>
                      <Link
                        to={classPath(c.class)}
                        className="mt-auto pt-3 text-sm font-medium text-accent-700 hover:text-accent-600 underline-offset-4 hover:underline"
                      >
                        AI prompt &amp; answer →
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        );
      })}

      {/* Roadmap */}
      <section id="coming-next" className="bg-cream border-t border-ink-200 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <h2 className="font-serif text-[1.7rem] text-ink-900 tracking-tight">Coming next</h2>
          <p className="text-sm text-ink-500 mt-1 mb-5">
            The {upcomingClasses.length} planned classes not made yet. Topics and order may still change.
          </p>
          <details className="group rounded-card border border-ink-200/80 bg-paper">
            <summary className="cursor-pointer select-none list-none px-5 py-4 flex items-center justify-between gap-3 font-medium text-ink-800">
              <span>Show all {upcomingClasses.length} planned classes</span>
              <span className="text-ink-400 transition-transform duration-150 group-open:rotate-180" aria-hidden="true">▾</span>
            </summary>
            <div className="px-5 pb-5 space-y-6">
              {excelModules.map((m) => {
                const items = upcomingClasses.filter((u) => u.module === m.name);
                if (items.length === 0) return null;
                return (
                  <div key={m.name}>
                    <h3 className="font-serif text-lg text-ink-900">
                      <span className="text-ink-500 text-sm font-sans mr-2">{moduleLabel(m)}</span>{m.name}
                    </h3>
                    <ol className="mt-2 divide-y divide-ink-200/70 border-y border-ink-200/70">
                      {items.map((u) => (
                        <li key={u.class} className="py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
                          <span className="w-16 shrink-0 text-ink-500 tabular-nums">Class {u.class}</span>
                          <span className="flex-1 min-w-[10rem] text-ink-800">{u.topic}</span>
                          {u.needs_excel && <span className="text-xs text-ink-500">{needsLabel(u.needs_excel)}</span>}
                        </li>
                      ))}
                    </ol>
                  </div>
                );
              })}
            </div>
          </details>
        </div>
      </section>
    </div>
  );
}

function ModuleTile({ m }: { m: ExcelModule }) {
  const total = moduleTotal(m);
  const done = m.made >= total;
  const body = (
    <>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-500">{moduleLabel(m)}</span>
        <span className={`text-xs tabular-nums ${done ? 'text-econ-600 font-medium' : 'text-ink-500'}`}>
          {m.made}/{total}{done ? ' ✓' : ''}
        </span>
      </div>
      <div className="mt-1 font-serif text-[1.05rem] text-ink-900 leading-snug">{m.name}</div>
      <div className="mt-2 flex items-center gap-2">
        <LevelPill level={m.level} />
        <span className="text-xs text-ink-500">Classes {m.classes.replace('-', '–')}</span>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-ink-100 overflow-hidden" aria-hidden="true">
        <div className="h-full rounded-full bg-accent-500" style={{ width: `${(m.made / total) * 100}%` }} />
      </div>
    </>
  );
  const cls = 'block h-full rounded-card border border-ink-200/80 bg-cream p-4 shadow-card';
  return m.made > 0 ? (
    <a href={`#${moduleAnchor(m)}`} className={`${cls} aces-card-hover hover:border-accent-500/50`}>{body}</a>
  ) : (
    <a href="#coming-next" className={`${cls} aces-card-hover opacity-90`}>{body}</a>
  );
}
